import React, {
  memo,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { faAdd } from "@fortawesome/pro-regular-svg-icons";
import {
  faArrowUpRightFromSquare,
  faGripDotsVertical,
  faPenToSquare,
} from "@fortawesome/pro-solid-svg-icons";
import { useVirtualizer } from "@tanstack/react-virtual";
import { noop } from "lodash-es";
import {
  FieldError,
  FieldErrorsImpl,
  FormProvider,
  Merge,
  UseFieldArrayReturn,
  UseFormReturn,
} from "react-hook-form";

import { Button } from "src/base-components/Button";
import { useDiffViewContext } from "src/changeHistory/DiffViewModal/DiffViewContext";
import { Icon } from "src/design-system/Icon";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { SchemaOptions } from "src/router/SearchParams";
import { SchemaEditRow } from "src/schema/SchemaEditRow";
import { SchemaEmptyState } from "src/schema/SchemaEmptyState";
import {
  getNewProperty,
  PropertyUI,
  SchemaUI,
} from "src/schema/schemaMappingUtils";
import { useFlowJsonSchemaWindow } from "src/schema/window/useFlowJsonSchemaWindow";
import { useManifestNodeBuilderWindow } from "src/schema/window/useManifestNodeBuilderWindow";

type CustomSchemaEditorProps = {
  isShowingInputSchema: boolean;
  isReadonly: boolean;
  type: SchemaOptions;
  formMethods: UseFormReturn<SchemaUI>;
  propertiesFieldArrayReturn: UseFieldArrayReturn<SchemaUI, "properties", "id">;
  scrollContainerRef: RefObject<HTMLElement>;
};

const SchemaFieldDragOverlay: React.FC<{
  fieldName?: string;
}> = ({ fieldName }) => {
  return (
    <div className="flex w-fit min-w-[180px] cursor-default items-center gap-2 rounded-lg bg-white p-4 font-inter-medium-13px text-gray-700 shadow-lg">
      <Icon icon={faGripDotsVertical} size="xs" />
      {fieldName || "Field"}
    </div>
  );
};

const CustomSchemaEditorComponent: React.FC<CustomSchemaEditorProps> = ({
  isShowingInputSchema,
  isReadonly,
  type,
  formMethods,
  propertiesFieldArrayReturn,
  scrollContainerRef,
}) => {
  const { getValues } = formMethods;
  const { fields, append, move } = propertiesFieldArrayReturn;
  const { renderedInDiffView } = useDiffViewContext();
  const { setValue, subscribe } = formMethods;
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
  };

  const {
    openWindow,
    closeWindow,
    isOpen: isWindowOpen,
  } = useFlowJsonSchemaWindow();
  const {
    openWindow: openBuilderWindow,
    closeWindow: closeBuilderWindow,
    isOpen: isBuilderWindowOpen,
  } = useManifestNodeBuilderWindow();
  const [isJsonSchemaDirty, setIsJsonSchemaDirty] = useState(false);

  const onAddField = useCallback(() => {
    if (isReadonly) {
      return;
    }
    append(getNewProperty(getValues("properties")));
  }, [isReadonly, append, getValues]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragId(null);
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const fieldToMove = fields.findIndex((field) => field.id === active.id);
        const fieldToInsert = fields.findIndex((field) => field.id === over.id);
        if (fieldToMove !== -1 && fieldToInsert !== -1) {
          move(fieldToMove, fieldToInsert);
        }
      }
    },
    [fields, move],
  );

  const handleOpenWindow = useCallback(
    (highlightPath?: string) => {
      const value = getValues();

      if (value.properties.length === 0) {
        append(getNewProperty(value.properties));
      }

      openWindow({
        highlightPath,
        intialValue: structuredClone(value),
        schemaType: type,
        onClose: () => {
          setIsJsonSchemaDirty(false);
        },
        onEdit: (isDirty) => {
          setIsJsonSchemaDirty(isDirty);
        },
        onChange: (value) => {
          Object.keys(value).forEach((key) => {
            setValue(key as keyof SchemaUI, value[key as keyof SchemaUI]);
          });
        },
        subscribe: (callback) =>
          subscribe({
            formState: { values: true },
            callback: ({ values }) => {
              callback(values);
            },
          }),
      });
    },
    [setValue, subscribe, openWindow, getValues, append, type],
  );

  const handleOpenBuilderWindow = useCallback(() => {
    const value = getValues();

    if (value.properties.length === 0) {
      append(getNewProperty(value.properties));
    }

    openBuilderWindow({
      initialValue: structuredClone(value),
      schemaType: type,
      onClose: () => {},
      onChange: (value) => {
        Object.keys(value).forEach((key) => {
          setValue(key as keyof SchemaUI, value[key as keyof SchemaUI]);
        });
      },
    });
  }, [setValue, openBuilderWindow, getValues, append, type]);

  useEffect(() => {
    return () => {
      closeWindow();
      closeBuilderWindow();
    };
  }, [closeWindow, closeBuilderWindow]);

  const isManifestBuilderEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.manifestBuilder,
  );

  return (
    <>
      {fields.length > 0 && (
        <>
          <h3 className="mb-4 flex justify-between gap-x-2 font-inter-semibold-13px">
            <p>Fields</p>

            {!renderedInDiffView && (
              <div className="flex gap-x-2">
                {isManifestBuilderEnabled && (
                  <Button
                    dataLoc="manifest-node-builder-button"
                    iconRight={faPenToSquare}
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      !isBuilderWindowOpen && handleOpenBuilderWindow()
                    }
                  >
                    Manifest Node Builder
                  </Button>
                )}
                <Button
                  dataLoc="schema-json-view-button"
                  iconRight={faArrowUpRightFromSquare}
                  size="sm"
                  variant="secondary"
                  onClick={() => !isWindowOpen && handleOpenWindow()}
                >
                  JSON view
                </Button>
              </div>
            )}
          </h3>
          <div className="flex flex-row items-center gap-x-2 font-inter-normal-12px text-gray-500">
            <div className="basis-[20px]" />
            <div className="grow">Name</div>
            <div className="w-[140px]">Type</div>
            <div
              className={isShowingInputSchema ? "basis-[82px]" : "basis-[52px]"}
            />
          </div>
        </>
      )}
      {fields.length === 0 && (
        <SchemaEmptyState
          handleAddField={onAddField}
          isReadonly={isReadonly || isJsonSchemaDirty}
          type={type}
          onJsonSchemaClick={isWindowOpen ? noop : handleOpenWindow}
          onManifestNodeBuilderClick={
            isBuilderWindowOpen ? noop : handleOpenBuilderWindow
          }
        />
      )}
      <form>
        <DndContext
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.Always,
            },
          }}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
        >
          <SortableContext
            disabled={isReadonly || isJsonSchemaDirty}
            items={fields.map((param) => param.id)}
            strategy={verticalListSortingStrategy}
          >
            <FormProvider {...formMethods}>
              <VirtualizedSchemaEditRows
                formMethods={formMethods}
                handleOpenWindow={handleOpenWindow}
                isJsonSchemaDirty={isJsonSchemaDirty}
                isReadonly={isReadonly}
                isShowingInputSchema={isShowingInputSchema}
                isWindowOpen={isWindowOpen}
                propertiesFieldArrayReturn={propertiesFieldArrayReturn}
                scrollContainerRef={scrollContainerRef}
                type={type}
              />
            </FormProvider>
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            <SchemaFieldDragOverlay
              fieldName={
                fields.find((field) => field.id === activeDragId)?.fieldName
              }
            />
          </DragOverlay>
        </DndContext>
        {fields.length > 0 && (
          <div className="mt-5 mb-8">
            <Button
              dataLoc="add-schema-field"
              disabled={isReadonly || isJsonSchemaDirty}
              iconLeft={faAdd}
              variant="secondary"
              fullWidth
              onClick={onAddField}
            >
              Add field
            </Button>
          </div>
        )}
      </form>
    </>
  );
};

const VirtualizedSchemaEditRows: React.FC<
  CustomSchemaEditorProps & {
    isJsonSchemaDirty: boolean;
    isWindowOpen: boolean;
    handleOpenWindow: (highlightPath?: string) => void;
  }
> = memo(
  ({
    propertiesFieldArrayReturn,
    scrollContainerRef,
    formMethods,
    type,
    isShowingInputSchema,
    isReadonly,
    isJsonSchemaDirty,
    isWindowOpen,
    handleOpenWindow,
  }) => {
    const { renderedInDiffView } = useDiffViewContext();
    const { fields, remove } = propertiesFieldArrayReturn;

    const rowVirtualizer = useVirtualizer({
      enabled: !renderedInDiffView,
      count: fields.length,
      gap: 12,
      getScrollElement: () => scrollContainerRef.current,
      estimateSize: () => 34,
      overscan: 10,
      getItemKey: (index) => fields[index]?.id,
      measureElement: (element) => {
        return element.getBoundingClientRect().height;
      },
    });
    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalRowSize = rowVirtualizer.getTotalSize();
    const paddingTop = virtualRows?.[0]?.start || 0;
    const paddingBottom =
      totalRowSize - (virtualRows?.[virtualRows.length - 1]?.end || 0);

    const rows = renderedInDiffView
      ? fields.map((field, index) => ({ index, key: field.fieldName }))
      : virtualRows;

    return (
      <>
        {paddingTop > 0 && <div className="relative" style={{ paddingTop }} />}
        {rows.map(({ index, key }) => {
          const field = fields[index];
          if (!field) {
            return null;
          }
          return (
            <SchemaEditRow
              key={key}
              ref={rowVirtualizer.measureElement}
              displaySensitiveToggle={isShowingInputSchema}
              errorStatus={
                formMethods.formState.errors.properties?.at?.(index) as Merge<
                  FieldError,
                  FieldErrorsImpl<PropertyUI>
                >
              }
              fieldIndex={index}
              id={field.id}
              openJsonSchemaWindow={isWindowOpen ? noop : handleOpenWindow}
              property={field}
              readonly={isReadonly || isJsonSchemaDirty}
              type={type}
              onDelete={() => remove(index)}
              onShowAllEnums={() => {
                const field = formMethods.getValues(`properties.${index}`);
                handleOpenWindow(`properties.${field.fieldName}.enum`);
              }}
            />
          );
        })}
        {paddingBottom > 0 && (
          <div className="relative" style={{ paddingBottom }} />
        )}
      </>
    );
  },
);

export const CustomSchemaEditor = memo(CustomSchemaEditorComponent);
